import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CaisseController from './controllers/caisse-controller';
import DepotController from './controllers/depot-controller';
import UserController from './permissions/controller/user-controller';
import { isAdmin } from '../../login/controller/login-controller';
import CaisseClosureModal from './orders-components/CaisseClosureModal';

/* ----------------------------- helpers ----------------------------- */

const PLACEHOLDER_IMG = '/logo.jpg';
const PAGE_SIZE = 2;

const formatTND = (n) => `${(Number(n) || 0).toFixed(2)} TND`;

const getProductInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'PR';
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() || '').join('');
};

const ProductCard = React.memo(function ProductCard({ product, canReturnOutOfStock, onAdd, isVariant = false }) {
  const key = `${product.productId}:${product.productVariantId ?? 0}`;
  const out = product.stock <= 0;
  const disabled = out && !canReturnOutOfStock;
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  return (
    <button
      type="button"
      key={key}
      onClick={() => onAdd(product)}
      disabled={disabled}
      style={{ contentVisibility: 'auto', containIntrinsicSize: '240px', contain: 'layout paint style' }}
      className={`group flex flex-col text-left bg-white rounded-xl border ${disabled ? 'border-gray-100 opacity-60' : 'border-gray-200'} transition-colors duration-150 ${isVariant ? 'ml-4 border-l-4 border-l-gray-400' : ''}`}
    >
      <div className="aspect-square w-full overflow-hidden rounded-t-xl bg-gray-100 relative">
        {product.enPromo && (
          <span className="absolute top-1 left-1 z-10 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow">
            Promo
          </span>
        )}
        {product.imageUrl && !imgError ? (
          <>
            <img
              src={product.imageUrl}
              alt={product.name}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              className={`h-full w-full object-cover ${imgLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
            />
            {!imgLoaded && (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#174540] to-[#0d2926] text-3xl font-bold tracking-wide text-white">
                {getProductInitials(product.name)}
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#174540] to-[#0d2926] text-3xl font-bold tracking-wide text-white">
            {getProductInitials(product.name)}
          </div>
        )}
      </div>
      <div className="p-2 flex-1 flex flex-col">
        <div className="text-sm font-medium text-gray-900 line-clamp-2">{product.name}</div>
        <div className="text-[11px] text-gray-500 mt-0.5">{product.reference}</div>
        <div className="mt-auto pt-2 flex items-center justify-between">
          <div className="flex flex-col leading-tight">
            <span className="text-base font-semibold text-[#174540]">{formatTND(product.prix)}</span>
            {product.enPromo && Number(product.prixOriginal) > Number(product.prix) && (
              <span className="text-[11px] text-gray-400 line-through">{formatTND(product.prixOriginal)}</span>
            )}
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${out ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-800'}`}>
            {out ? 'Rupture' : `Stock: ${product.stock}`}
          </span>
        </div>
      </div>
    </button>
  );
});

/* Helper: group products by productId for tree view */
const groupProductsByProductId = (products) => {
  const groups = {};
  products.forEach((p) => {
    if (!groups[p.productId]) {
      groups[p.productId] = { parent: null, variants: [] };
    }
    if (!p.productVariantId) {
      // This is the parent product (no variant)
      groups[p.productId].parent = p;
    } else {
      // This is a variant
      groups[p.productId].variants.push(p);
    }
  });
  return groups;
};

/* ------------------------- Open-caisse modal ----------------------- */

const OpenCaisseModal = ({ open, onConfirm, onCancel }) => {
  const [fondCaisse, setFondCaisse] = useState('0');
  const [depots, setDepots] = useState([]);
  const [depotId, setDepotId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const admin = isAdmin();
  const [users, setUsers] = useState([]);
  const [utilisateurId, setUtilisateurId] = useState('');

  useEffect(() => {
    if (!open) return;
    // Reset form state every time the modal is (re)opened so values from a
    // previous open/close cycle don't make the inputs look locked.
    setFondCaisse('0');
    setNotes('');
    setUtilisateurId('');
    setSaving(false);
    DepotController.getAllDepots()
      .then((arr) => {
        const list = arr || [];
        setDepots(list);
        const def = list.find((d) => d.isDefault) || list[0];
        if (def) setDepotId(String(def.id));
      })
      .catch(() => toast.error('Impossible de charger les dépôts'));
    if (admin) {
      UserController.fetchUsers(1, 500, '', 'Utilisateur')
        .then((res) => setUsers(res?.list || res?.items || res?.data || []))
        .catch(() => { /* non-blocking */ });
    }
  }, [open, admin]);

  if (!open) return null;

  const submit = async () => {
    if (!depotId) { toast.error('Sélectionnez un dépôt'); return; }
    if (fondCaisse === '' || isNaN(Number(fondCaisse))) { toast.error('Fond de caisse invalide'); return; }
    setSaving(true);
    try {
      await onConfirm({
        fondCaisse: Number(fondCaisse),
        depotId: Number(depotId),
        notes,
        utilisateurId: admin && utilisateurId ? Number(utilisateurId) : null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-5 bg-gradient-to-br from-[#174540] to-[#0d2926] flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-white">Ouvrir la caisse</h2>
            <p className="mt-1 text-sm text-emerald-100">
              Aucune session de caisse n'est ouverte. Renseignez le fond de caisse et le dépôt pour démarrer.
            </p>
          </div>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              aria-label="Fermer"
              title="Fermer et retourner au tableau de bord"
              className="text-white/80 hover:text-white shrink-0"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fond de caisse (TND)</label>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={fondCaisse}
              onChange={(e) => setFondCaisse(e.target.value)}
              className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#174540] focus:border-[#174540]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dépôt <span className="text-red-500">*</span></label>
            <select
              value={depotId}
              onChange={(e) => setDepotId(e.target.value)}
              className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#174540] focus:border-[#174540]"
            >
              <option value="" disabled>Sélectionner un dépôt</option>
              {(() => {
                const selectedUser = admin && utilisateurId
                  ? users.find((u) => String(u.id) === String(utilisateurId))
                  : null;
                const filtered = selectedUser && selectedUser.depotId
                  ? depots.filter((d) => d.id === selectedUser.depotId)
                  : depots;
                return filtered.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}{d.isDefault ? ' (défaut)' : ''}</option>
                ));
              })()}
            </select>
          </div>

          {admin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Utilisateur (optionnel)</label>
              <select
                value={utilisateurId}
                onChange={(e) => {
                  const v = e.target.value;
                  setUtilisateurId(v);
                  // If the selected user has an assigned depot, lock the depot
                  // dropdown to it. Otherwise leave the current selection alone.
                  const u = users.find((x) => String(x.id) === String(v));
                  if (u && u.depotId) setDepotId(String(u.depotId));
                }}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#174540] focus:border-[#174540]"
              >
                <option value="">Moi-même</option>
                {users.map((u) => {
                  const label = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.userName || u.email || `#${u.id}`;
                  return <option key={u.id} value={u.id}>{label}</option>;
                })}
              </select>
              <p className="mt-1 text-xs text-gray-500">En tant qu'administrateur, vous pouvez ouvrir une caisse pour un autre utilisateur. Le dépôt est limité au dépôt de l'utilisateur choisi.</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optionnel)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#174540] focus:border-[#174540]"
            />
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-5 py-3 text-base font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
            >
              Annuler
            </button>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-6 py-3 text-base font-semibold rounded-lg text-white bg-[#174540] hover:bg-[#0d2926] disabled:opacity-60"
          >
            {saving ? 'Ouverture…' : 'Ouvrir la caisse'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* --------------------------- Checkout modal ------------------------ */

const CheckoutModal = ({ open, total, onConfirm, onCancel, saving, alreadyPaid = 0, editingRef = null }) => {
  const [paye, setPaye] = useState('');
  const [client, setClient] = useState({ firstName: '', lastName: '', number: '' });

  const reste = Math.max(0, total - alreadyPaid);

  useEffect(() => {
    if (open) { setPaye(reste ? String(reste.toFixed(2)) : '0'); setClient({ firstName: '', lastName: '', number: '' }); }
  }, [open, total, alreadyPaid]);

  if (!open) return null;
  const payeNum = Number(paye) || 0;
  const rendu = Math.max(0, payeNum - reste);
  const isPreorder = payeNum < reste;
  const clientValid = client.firstName.trim() && client.lastName.trim() && client.number.trim();
  const canConfirm = payeNum > 0 && (!isPreorder || clientValid);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-5 bg-gradient-to-br from-[#174540] to-[#0d2926]">
          <h2 className="text-xl font-semibold text-white">
            {editingRef ? `Encaisser précommande ${editingRef}` : 'Encaisser'}
          </h2>
          <p className="text-sm text-emerald-100 mt-1">Total : <span className="font-bold text-white">{formatTND(total)}</span></p>
          {alreadyPaid > 0 && (
            <p className="text-sm text-emerald-100">Déjà payé : <span className="font-bold text-white">{formatTND(alreadyPaid)}</span> • Reste : <span className="font-bold text-white">{formatTND(reste)}</span></p>
          )}
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant reçu maintenant (Espèces)</label>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={paye}
              onChange={(e) => setPaye(e.target.value)}
              className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#174540] focus:border-[#174540]"
            />
            <div className="mt-2 flex flex-wrap gap-2">
              {[5, 10, 20, 50, 100].map((n) => (
                <button key={n} type="button" onClick={() => setPaye(String(n))} className="px-3 py-2 text-sm rounded-md bg-gray-100 hover:bg-gray-200">
                  {n} TND
                </button>
              ))}
              <button type="button" onClick={() => setPaye(String(reste.toFixed(2)))} className="px-3 py-2 text-sm rounded-md bg-emerald-50 text-emerald-800 hover:bg-emerald-100">
                Compte juste
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Prénom client {isPreorder ? <span className="text-red-600">*</span> : '(optionnel)'}
              </label>
              <input
                value={client.firstName}
                onChange={(e) => setClient({ ...client, firstName: e.target.value })}
                required={isPreorder}
                className={`w-full px-3 py-2 border rounded-md ${isPreorder && !client.firstName.trim() ? 'border-red-400' : 'border-gray-300'}`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Nom client {isPreorder ? <span className="text-red-600">*</span> : '(optionnel)'}
              </label>
              <input
                value={client.lastName}
                onChange={(e) => setClient({ ...client, lastName: e.target.value })}
                required={isPreorder}
                className={`w-full px-3 py-2 border rounded-md ${isPreorder && !client.lastName.trim() ? 'border-red-400' : 'border-gray-300'}`}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Téléphone {isPreorder ? <span className="text-red-600">*</span> : '(optionnel)'}
              </label>
              <input
                value={client.number}
                onChange={(e) => setClient({ ...client, number: e.target.value })}
                required={isPreorder}
                className={`w-full px-3 py-2 border rounded-md ${isPreorder && !client.number.trim() ? 'border-red-400' : 'border-gray-300'}`}
              />
            </div>
            {isPreorder && (
              <p className="col-span-2 text-xs text-amber-700">
                Pour une précommande (paiement partiel), le nom, prénom et téléphone du client sont obligatoires.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
            <div className="text-sm">
              <div className="text-gray-500">Reste après ce paiement</div>
              <div className={`text-lg font-semibold ${payeNum < reste ? 'text-amber-600' : 'text-gray-900'}`}>{formatTND(Math.max(0, reste - payeNum))}</div>
            </div>
            <div className="text-sm text-right">
              <div className="text-gray-500">Monnaie à rendre</div>
              <div className="text-lg font-semibold text-emerald-700">{formatTND(rendu)}</div>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
          <button type="button" onClick={onCancel} disabled={saving} className="px-5 py-3 text-base font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50">
            Annuler
          </button>
          <button
            type="button"
            disabled={saving || !canConfirm}
            onClick={() => {
              if (isPreorder && !clientValid) {
                toast.error('Prénom, nom et téléphone du client sont obligatoires pour une précommande');
                return;
              }
              onConfirm({
                modePaiement: 'cash',
                montantPaye: payeNum,
                montantRendu: rendu,
                client,
              });
            }}
            className={`px-6 py-3 text-base font-semibold rounded-lg text-white disabled:opacity-60 ${isPreorder ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
          >
            {saving ? 'Enregistrement…' : (isPreorder ? 'Précommande (partiel)' : 'Valider la vente')}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------ Page ------------------------------- */

const getLineUnitPrice = (item) => Number(item?.prixVente ?? item?.prix ?? 0);

const CaissePage = () => {
  const navigate = useNavigate();
  const [caisse, setCaisse] = useState(null);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [loadingCaisse, setLoadingCaisse] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [cart, setCart] = useState([]); // {key, productId, productVariantId, name, reference, prix, qty, stock, imageUrl}
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [savingSale, setSavingSale] = useState(false);
  const [editingPriceKey, setEditingPriceKey] = useState(null);
  const [priceDrafts, setPriceDrafts] = useState({});

  // --- Exchange (échange) mode ---
  const [exchangeMode, setExchangeMode] = useState(false);
  const [returnCart, setReturnCart] = useState([]); // returned products (same shape as cart)
  const [addTarget, setAddTarget] = useState('new'); // 'new' | 'return' — which cart receives a product click
  const [savingExchange, setSavingExchange] = useState(false);

  // When editing/finalizing a preorder, holds {id, reference, montantInitialPaye, caisseId}
  const [editingPreorder, setEditingPreorder] = useState(null);

  const searchInputRef = useRef(null);

  /* --- caisse session: load on mount; prompt if none --- */
  const loadCaisse = useCallback(async () => {
    setLoadingCaisse(true);
    try {
      const c = await CaisseController.current();
      setCaisse(c || null);
      setShowOpenModal(!c);
    } catch (e) {
      console.error(e);
      toast.error('Impossible de charger la caisse');
    } finally {
      setLoadingCaisse(false);
    }
  }, []);

  useEffect(() => { loadCaisse(); }, [loadCaisse]);

  /* --- products: load when caisse open / search / page changes --- */
  const loadProducts = useCallback(async () => {
    if (!caisse) return;
    setLoadingProducts(true);
    try {
      const data = await CaisseController.searchProducts(page, PAGE_SIZE, searchTerm, caisse.depotId);
      const list = (data.list || []).slice().sort((a, b) => (b.stock || 0) - (a.stock || 0));
      setProducts(list);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error('Erreur de chargement des produits');
    } finally {
      setLoadingProducts(false);
    }
  }, [caisse, page, searchTerm]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  // Auto-focus search field
  useEffect(() => {
    if (caisse) searchInputRef.current?.focus();
  }, [caisse]);

  // Debounced search
  const [searchDraft, setSearchDraft] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearchTerm(searchDraft.trim()); }, 250);
    return () => clearTimeout(t);
  }, [searchDraft]);

  /* --- cart ops --- */
  const keyOf = (p) => `${p.productId}:${p.productVariantId ?? 0}`;

  const addReturnItem = useCallback((p) => {
    setReturnCart((prev) => {
      const k = `${p.productId}:${p.productVariantId ?? 0}`;
      const ex = prev.find((it) => it.key === k);
      if (ex) return prev.map((it) => it.key === k ? { ...it, qty: it.qty + 1 } : it);
      return [...prev, {
        key: k,
        productId: p.productId,
        productVariantId: p.productVariantId,
        name: p.name,
        reference: p.reference,
        prix: p.prix,
        prixVente: p.prix,
        prixOriginal: p.prixOriginal,
        enPromo: p.enPromo,
        qty: 1,
        stock: p.stock,
        imageUrl: p.imageUrl,
      }];
    });
  }, []);

  const addToCart = useCallback((p) => {
    // In exchange mode, a product click can target the "returned" list instead.
    if (exchangeMode && addTarget === 'return') { addReturnItem(p); return; }
    if (p.stock <= 0) { toast.error('Stock épuisé'); return; }
    setCart((prev) => {
      const k = keyOf(p);
      const ex = prev.find((it) => it.key === k);
      if (ex) {
        if (ex.qty + 1 > p.stock) { toast.warn('Stock maximum atteint'); return prev; }
        return prev.map((it) => it.key === k ? { ...it, qty: it.qty + 1 } : it);
      }
      return [...prev, {
        key: k,
        productId: p.productId,
        productVariantId: p.productVariantId,
        name: p.name,
        reference: p.reference,
        prix: p.prix,
        prixVente: p.prix,
        prixOriginal: p.prixOriginal,
        enPromo: p.enPromo,
        qty: 1,
        stock: p.stock,
        imageUrl: p.imageUrl,
      }];
    });
  }, [addReturnItem, addTarget, exchangeMode]);

  const changeQty = (k, delta) => {
    setCart((prev) => prev.map((it) => {
      if (it.key !== k) return it;
      const q = Math.max(0, it.qty + delta);
      if (q > it.stock) { toast.warn('Stock maximum atteint'); return it; }
      return { ...it, qty: q };
    }).filter((it) => it.qty > 0));
  };

  const removeFromCart = (k) => setCart((prev) => prev.filter((it) => it.key !== k));
  const clearCart = () => setCart([]);

  const openPriceEditor = (item) => {
    const currentPrice = getLineUnitPrice(item);
    setEditingPriceKey(item.key);
    setPriceDrafts((prev) => ({ ...prev, [item.key]: String(currentPrice.toFixed(3)) }));
  };

  const saveLinePrice = (key) => {
    const nextPrice = Number(priceDrafts[key]);
    if (!Number.isFinite(nextPrice) || nextPrice < 0) {
      toast.error('Prix vente invalide');
      return;
    }
    setCart((prev) => prev.map((it) => (it.key === key ? { ...it, prixVente: nextPrice } : it)));
    setPriceDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setEditingPriceKey(null);
  };

  const resetLinePrice = (key) => {
    setCart((prev) => prev.map((it) => (it.key === key ? { ...it, prixVente: it.prix } : it)));
    setPriceDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (editingPriceKey === key) setEditingPriceKey(null);
  };

  const cancelPriceEditor = (key) => {
    setEditingPriceKey((prev) => (prev === key ? null : prev));
    setPriceDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  /* --- return-cart ops (exchange) --- */
  const changeReturnQty = (k, delta) => {
    setReturnCart((prev) => prev.map((it) => {
      if (it.key !== k) return it;
      const q = Math.max(0, it.qty + delta);
      return { ...it, qty: q };
    }).filter((it) => it.qty > 0));
  };
  const removeFromReturn = (k) => setReturnCart((prev) => prev.filter((it) => it.key !== k));
  const clearReturnCart = () => setReturnCart([]);

  const totals = useMemo(() => {
    const totalTTC = cart.reduce((s, it) => s + getLineUnitPrice(it) * it.qty, 0);
    const totalItems = cart.reduce((s, it) => s + it.qty, 0);
    return { totalTTC, totalItems };
  }, [cart]);

  /* --- exchange totals: difference = new products − returned products --- */
  const exchangeTotals = useMemo(() => {
    const returnTotal = returnCart.reduce((s, it) => s + getLineUnitPrice(it) * it.qty, 0);
    const newTotal = cart.reduce((s, it) => s + getLineUnitPrice(it) * it.qty, 0);
    return { returnTotal, newTotal, difference: newTotal - returnTotal };
  }, [cart, returnCart]);

  const startExchange = () => {
    setExchangeMode(true);
    setAddTarget('return');
  };
  const cancelExchange = () => {
    setExchangeMode(false);
    setAddTarget('new');
    clearReturnCart();
    clearCart();
  };

  /* --- barcode scanner shortcut: on Enter in search, if exactly 1 result and stock>0, add it --- */
  const onSearchKeyDown = async (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const term = (searchDraft || '').trim();

    // Fast path: if results already loaded for this exact term, use them
    if (term && term === searchTerm && !loadingProducts) {
      if (products.length === 1 && products[0].stock > 0) {
        addToCart(products[0]);
        setSearchDraft('');
        setTimeout(() => searchInputRef.current?.focus(), 50);
        return;
      }
      if (products.length === 0) {
        toast.warn('Produit non trouvé');
        return;
      }
    }

    // Slow path: debounce/search hasn't finished yet — do a direct lookup so
    // scans don't get lost when Enter is pressed before results arrive.
    if (!term || !caisse) return;
    try {
      const data = await CaisseController.searchProducts(1, PAGE_SIZE, term, caisse.depotId);
      const list = (data.list || []).slice().sort((a, b) => (b.stock || 0) - (a.stock || 0));
      // Prefer an exact barcode match if present (handles partial-name collisions)
      const exact = list.find((p) =>
        (p.codeABarre && String(p.codeABarre) === term) ||
        (p.reference && String(p.reference) === term)
      );
      const target = exact || (list.length === 1 ? list[0] : null);
      if (target) {
        if ((target.stock || 0) <= 0) { toast.error('Stock épuisé'); return; }
        addToCart(target);
        setSearchDraft('');
        setSearchTerm('');
        setPage(1);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      } else if (list.length === 0) {
        toast.warn('Produit non trouvé');
      } else {
        // Multiple results — surface them so the user can pick
        setProducts(list);
        setTotal(data.total || list.length);
        setSearchTerm(term);
      }
    } catch (err) {
      console.error(err);
      toast.error('Erreur de recherche');
    }
  };

  /* --- finalize sale --- */
  const confirmSale = async ({ modePaiement, montantPaye: cashReceivedNow, montantRendu, client }) => {
    if (!caisse) return;
    if (cart.length === 0) { toast.error('Panier vide'); return; }
    setSavingSale(true);
    try {
      const previousPaye = editingPreorder?.previousMontantPaye || 0;
      const newTotalPaid = previousPaye + cashReceivedNow;
      const cartTotal = totals.totalTTC;
      const fullyPaid = newTotalPaid >= cartTotal;
      // Cap the recorded paid amount at the cart total: cashier may type more
      // than the price just to compute the change, but we never store more than
      // what the products actually cost.
      const recordedMontantPaye = Math.min(newTotalPaid, cartTotal);
      const recordedMontantRendu = Math.max(0, newTotalPaid - cartTotal);

      const entet = {
        id: editingPreorder?.id || 0,
        caisseId: caisse.id,
        depotId: caisse.depotId,
        dateCommande: editingPreorder?.dateCommande || new Date().toISOString(),
        reference: editingPreorder?.reference || null,
        totalTTC: cartTotal,
        totalHt: 0,
        totalTva: 0,
        totalRemise: 0,
        modePaiement,
        montantPaye: recordedMontantPaye,
        montantRendu: recordedMontantRendu,
        etat: fullyPaid ? 'paid' : 'pending',
        clientFirstName: client?.firstName || editingPreorder?.clientFirstName || null,
        clientLastName: client?.lastName || editingPreorder?.clientLastName || null,
        clientNumber: client?.number || editingPreorder?.clientNumber || null,
      };
      const line = cart.map((it) => ({
        id: 0,
        productId: it.productId,
        productVariantId: it.productVariantId,
        quantite: it.qty,
        prixUnitaire: getLineUnitPrice(it),
        totalTTC: getLineUnitPrice(it) * it.qty,
        totalHt: 0,
        totalTva: 0,
        totalRemise: 0,
      }));
      const savedOrder = await CaisseController.saveCommande(entet, line);
      const isPreorder = !fullyPaid;
      toast.success(isPreorder ? 'Précommande enregistrée' : 'Vente enregistrée');

      // Print ticket (show effective payment numbers)
      printTicket({
        items: cart,
        totalTTC: cartTotal,
        montantPaye: recordedMontantPaye,
        montantRendu: recordedMontantRendu,
        client,
        reference: savedOrder?.reference,
        isPreorder,
        avancePrecedente: previousPaye,
        versementCourant: Math.min(cashReceivedNow, Math.max(0, cartTotal - previousPaye)),
        isFromPreorder: !!editingPreorder,
        preorderDate: editingPreorder?.dateCommande || null,
        preorderReference: editingPreorder?.reference || null,
      });

      clearCart();
      setEditingPreorder(null);
      setCheckoutOpen(false);
      // Refresh caisse total + stock view
      loadCaisse();
      loadProducts();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingSale(false);
    }
  };

  /* --- finalize exchange (échange) --- */
  const confirmExchange = async () => {
    if (!caisse) return;
    if (returnCart.length === 0 && cart.length === 0) {
      toast.error('Ajoutez les produits retournés et les nouveaux produits');
      return;
    }
    const { returnTotal, newTotal, difference } = exchangeTotals;
    setSavingExchange(true);
    try {
      const payload = {
        caisseId: caisse.id,
        depotId: caisse.depotId,
        originalReference: null,
        returnedLines: returnCart.map((it) => ({
          productId: it.productId,
          productVariantId: it.productVariantId,
          quantite: it.qty,
            prixUnitaire: getLineUnitPrice(it),
        })),
        newLines: cart.map((it) => ({
          productId: it.productId,
          productVariantId: it.productVariantId,
          quantite: it.qty,
            prixUnitaire: getLineUnitPrice(it),
        })),
        modePaiement: 'cash',
        montantPaye: difference > 0 ? difference : 0,
        montantRendu: difference < 0 ? Math.abs(difference) : 0,
        clientFirstName: null,
        clientLastName: null,
        clientNumber: null,
      };
      const saved = await CaisseController.echange(payload);
      toast.success('Échange enregistré');

      printExchangeTicket({
        returnedItems: returnCart,
        newItems: cart,
        returnTotal,
        newTotal,
        difference,
        reference: saved?.reference,
      });

      cancelExchange();
      loadCaisse();
      loadProducts();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'échange');
    } finally {
      setSavingExchange(false);
    }
  };

  /* --- print exchange ticket --- */
  const printExchangeTicket = ({ returnedItems, newItems, returnTotal, newTotal, difference, reference }) => {
    const now = new Date();
    const rows = (items) => items.map((it, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${it.name}${it.reference ? `<div style="font-size:11px;font-weight:700">${it.reference}</div>` : ''}</td>
        <td style="text-align:center">${it.qty}</td>
        <td style="text-align:right">${(getLineUnitPrice(it) * it.qty).toFixed(3)}</td>
      </tr>`).join('');

    const diffLabel = difference > 0 ? 'Le client paie' : difference < 0 ? 'À rendre au client' : 'Aucune différence';
    const diffValue = Math.abs(difference).toFixed(3);

    const printHtml = `<!doctype html><html><head><meta charset="utf-8"/>
      <title>Échange ${reference || ''}</title>
      <style>
        @page { size: 80mm auto; margin: 0; }
        * { font-family: 'Arial Black', Arial, sans-serif; }
        body { width: 80mm; margin: 0; padding: 6px 8px; color: #000; }
        .center { text-align: center; }
        .brand { font-size: 22px; font-weight: 900; letter-spacing: 1px; }
        .muted { font-size: 11px; }
        h3 { font-size: 13px; margin: 8px 0 2px; border-bottom: 1px dashed #000; padding-bottom: 2px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { padding: 2px 0; vertical-align: top; }
        .row { display: flex; justify-content: space-between; font-size: 13px; margin-top: 2px; }
        .row-big { display: flex; justify-content: space-between; font-size: 16px; font-weight: 900; margin-top: 6px; border-top: 2px solid #000; padding-top: 4px; }
        .dashed { border-top: 1px dashed #000; margin: 6px 0; }
      </style></head><body>
      <div class="center brand">TENDRESSE</div>
      <div class="center muted">ÉCHANGE</div>
      <div class="center muted">${reference || ''}</div>
      <div class="center muted">${now.toLocaleString('fr-FR')}</div>
      ${returnedItems.length ? `<h3>Produits retournés</h3>
      <table><thead><tr><th>#</th><th>Article</th><th style="text-align:center">Qte</th><th style="text-align:right">Total</th></tr></thead>
      <tbody>${rows(returnedItems)}</tbody></table>
      <div class="row"><span>Total retours</span><span>${returnTotal.toFixed(3)} TND</span></div>` : ''}
      ${newItems.length ? `<h3>Nouveaux produits</h3>
      <table><thead><tr><th>#</th><th>Article</th><th style="text-align:center">Qte</th><th style="text-align:right">Total</th></tr></thead>
      <tbody>${rows(newItems)}</tbody></table>
      <div class="row"><span>Total nouveaux</span><span>${newTotal.toFixed(3)} TND</span></div>` : ''}
      <div class="row-big"><span>${diffLabel}</span><span>${diffValue} TND</span></div>
      <div class="dashed"></div>
      <div class="center muted">Merci de votre visite</div>
      </body></html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 500);
    };

    iframe.onload = () => {
      const win = iframe.contentWindow;
      if (!win) { cleanup(); return; }
      win.onafterprint = cleanup;
      setTimeout(() => { win.focus(); win.print(); }, 150);
      setTimeout(cleanup, 60000);
    };

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(printHtml);
    doc.close();
  };

  /* --- print ticket --- */
  const printTicket = ({
    items,
    totalTTC,
    montantPaye,
    montantRendu,
    client,
    reference,
    isPreorder = false,
    avancePrecedente = 0,
    versementCourant = 0,
    isFromPreorder = false,
    preorderDate = null,
    preorderReference = null,
  }) => {
    const now = new Date();

    const rows = items.map((it, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${it.name}${it.reference ? `<div style="font-size:11px;font-weight:700">${it.reference}</div>` : ''}</td>
        <td style="text-align:center">${it.qty}</td>
        <td style="text-align:right">${(getLineUnitPrice(it) * it.qty).toFixed(3)}</td>
      </tr>`).join('');

    const resteAPayer = Math.max(0, totalTTC - montantPaye);
    const fmtDate = (d) => {
      const dt = new Date(d);
      return `${dt.toLocaleDateString('fr-FR')} ${dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    };

    // Payment block: depends on whether this is a fresh sale, fresh preorder,
    // a partial top-up on a preorder, or the final settlement of a preorder.
    let paymentBlock = '';
    if (isPreorder && !isFromPreorder) {
      // Fresh preorder: Total / Avance / Reste
      paymentBlock = `
        <div class="row"><span>Avance</span><span>${montantPaye.toFixed(3)} TND</span></div>
        <div class="row bold"><span>Reste a payer</span><span>${resteAPayer.toFixed(3)} TND</span></div>`;
    } else if (isPreorder && isFromPreorder) {
      // Top-up on existing preorder, still not fully paid
      paymentBlock = `
        <div class="row"><span>Avance precedente</span><span>${avancePrecedente.toFixed(3)} TND</span></div>
        <div class="row"><span>Versement</span><span>${versementCourant.toFixed(3)} TND</span></div>
        <div class="row bold"><span>Total paye</span><span>${montantPaye.toFixed(3)} TND</span></div>
        <div class="row bold"><span>Reste a payer</span><span>${resteAPayer.toFixed(3)} TND</span></div>`;
    } else if (!isPreorder && isFromPreorder) {
      // Final settlement of a preorder
      paymentBlock = `
        <div class="row"><span>Avance precedente</span><span>${avancePrecedente.toFixed(3)} TND</span></div>
        <div class="row"><span>Versement final</span><span>${versementCourant.toFixed(3)} TND</span></div>
        <div class="row bold"><span>Total paye</span><span>${montantPaye.toFixed(3)} TND</span></div>
        <div class="row"><span>Rendu</span><span>${montantRendu.toFixed(3)} TND</span></div>`;
    } else {
      // Fresh, fully paid sale
      paymentBlock = `
        <div class="row"><span>Paye</span><span>${montantPaye.toFixed(3)} TND</span></div>
        <div class="row"><span>Rendu</span><span>${montantRendu.toFixed(3)} TND</span></div>`;
    }

    // Header banner
    let banner = '';
    if (isPreorder && !isFromPreorder) banner = '<div class="center preorder">*** PRECOMMANDE ***</div>';
    else if (isPreorder && isFromPreorder) banner = '<div class="center preorder">*** PRECOMMANDE - VERSEMENT ***</div>';
    else if (!isPreorder && isFromPreorder) banner = '<div class="center preorder">*** REGLEMENT PRECOMMANDE ***</div>';

    // Original preorder info (when this ticket is for a top-up or settlement)
    const preorderInfo = isFromPreorder ? `
        <div class="center meta">Precommande du ${preorderDate ? fmtDate(preorderDate) : '-'}</div>
        ${preorderReference ? `<div class="center meta">Ref precommande: ${preorderReference}</div>` : ''}
      ` : '';

    // Footer message
    let footer;
    if (isPreorder && !isFromPreorder) footer = 'Precommande - a regler ulterieurement';
    else if (isPreorder && isFromPreorder) footer = 'Versement enregistre - reste a payer';
    else if (!isPreorder && isFromPreorder) footer = 'Precommande reglee - Merci';
    else footer = 'Merci pour votre achat';

    const printHtml = `<!DOCTYPE html><html><head><title>Ticket</title>
      <style>
        @page { size: 80mm auto; margin: 2mm; }
        * { margin:0; padding:0; box-sizing:border-box; color:#000 !important; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
        body { font-family:'Arial Black','Helvetica',sans-serif; font-size:15px; font-weight:700; width:76mm; padding:2mm; color:#000; line-height:1.35; }
        .center { text-align:center; }
        .sep { border-top:2px dashed #000; margin:6px 0; }
        .bold { font-weight:900; }
        .row { display:flex; justify-content:space-between; padding:2px 0; font-size:15px; font-weight:700; }
        .row-big { display:flex; justify-content:space-between; font-weight:900; font-size:20px; padding:5px 0; }
        table { width:100%; border-collapse:collapse; font-size:14px; font-weight:700; }
        th { text-align:left; padding:3px 2px; border-bottom:2px solid #000; font-size:14px; font-weight:900; }
        td { padding:4px 2px; border-bottom:1px solid #000; font-size:14px; font-weight:700; vertical-align:top; }
        .title { font-size:26px; font-weight:900; letter-spacing:1px; }
        .preorder { font-size:18px; font-weight:900; margin-top:3px; }
        .meta { font-size:13px; font-weight:700; margin-top:3px; }
      </style></head><body>
        <div class="center title">TENDRESSE</div>
        ${banner}
        <div class="center meta">${fmtDate(now)}</div>
        ${reference ? `<div class="center meta">Ref: ${reference}</div>` : ''}
        ${preorderInfo}
        ${client?.firstName || client?.lastName ? `<div class="center meta">Client: ${(client.firstName||'')} ${(client.lastName||'')}</div>` : ''}
        <div class="sep"></div>
        <table>
          <thead><tr><th>#</th><th>Article</th><th style="text-align:center">Qte</th><th style="text-align:right">Total</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="sep"></div>
        <div class="row-big"><span>TOTAL TTC</span><span>${totalTTC.toFixed(3)} TND</span></div>
        ${paymentBlock}
        <div class="sep"></div>
        <div class="center" style="font-size:14px;font-weight:900;margin-top:5px">${footer}</div>
        <div class="center" style="font-size:13px;font-weight:700;margin-top:2px">Tendresse</div>
      </body></html>`;

    // Print via a hidden iframe so the caissier never leaves the page.
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);
    const cleanup = () => { try { document.body.removeChild(iframe); } catch (e) {} };
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        setTimeout(() => {
          try { win.focus(); win.print(); } catch (e) { console.error(e); }
          // Browsers fire afterprint on the iframe's window; fall back to a timer.
          if (win.matchMedia) {
            const mql = win.matchMedia('print');
            const handler = (m) => { if (!m.matches) { mql.removeListener && mql.removeListener(handler); setTimeout(cleanup, 200); } };
            mql.addListener && mql.addListener(handler);
          }
          win.onafterprint = () => setTimeout(cleanup, 200);
          setTimeout(cleanup, 60000); // safety: never leak iframes
        }, 150);
      } catch (e) {
        console.error('ticket print failed', e);
        cleanup();
      }
    };
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(printHtml);
    doc.close();
  };

  /* --- preorder (pending orders) --- */
  const [preorders, setPreorders] = useState([]);
  const [showPreorders, setShowPreorders] = useState(false);
  const [loadingPreorders, setLoadingPreorders] = useState(false);
  const [preorderSearch, setPreorderSearch] = useState('');

  const loadPreorders = async () => {
    setLoadingPreorders(true);
    try {
      const result = await CaisseController.paginateCommandes(null, 1, 100, 'pending');
      setPreorders(result?.list || []);
      setShowPreorders(true);
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du chargement des précommandes');
    } finally {
      setLoadingPreorders(false);
    }
  };

  const payPreorder = async (order) => {
    if (!caisse) return;
    if (cart.length > 0 && !window.confirm('Le panier actuel sera vidé. Continuer ?')) return;
    try {
      // Load the preorder lines into the cart for editing/finalization
      const full = await CaisseController.getCommande(order.id);
      if (!full) { toast.error('Commande introuvable'); return; }

      const entet = full.entet || full;
      const lines = full.line || [];

      const cartItems = lines.map((l) => {
        const productName = l.product?.name || `#${l.productId}`;
        const variantName = l.productVariant?.name;
        const reference = l.productVariant?.reference || l.product?.reference || '';
        return {
          key: `${l.productId}:${l.productVariantId ?? 0}`,
          productId: l.productId,
          productVariantId: l.productVariantId,
          name: variantName ? `${productName} — ${variantName}` : productName,
          reference,
          prix: l.prixUnitaire,
          qty: l.quantite,
          // Stock will not be validated client-side for preloaded lines (set high so qty edit is free)
          stock: 9999,
          imageUrl: null,
        };
      });

      setCart(cartItems);
      setEditingPreorder({
        id: entet.id,
        reference: entet.reference,
        dateCommande: entet.dateCommande,
        previousMontantPaye: entet.montantPaye || 0,
        clientFirstName: entet.clientFirstName,
        clientLastName: entet.clientLastName,
        clientNumber: entet.clientNumber,
        caisseId: entet.caisseId,
      });
      setShowPreorders(false);
      toast.info(`Précommande ${entet.reference} chargée — modifiez puis cliquez Encaisser`);
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du chargement de la précommande');
    }
  };

  const cancelEditingPreorder = () => {
    setEditingPreorder(null);
    clearCart();
  };

  /* --- close caisse --- */
  const [showClosureModal, setShowClosureModal] = useState(false);
  // Bumped each time we open the closure or open-caisse modal, used as a React
  // key so the modal component remounts fresh between successive close cycles.
  const [caisseCycle, setCaisseCycle] = useState(0);

  const closeCaisse = () => {
    if (!caisse) return;
    if (cart.length > 0 && !window.confirm('Le panier est non vide. Le vider et fermer la caisse ?')) return;
    setCaisseCycle((n) => n + 1);
    setShowClosureModal(true);
  };

  const handleClosureModalClose = async () => {
    setShowClosureModal(false);
    // Check if caisse was actually closed
    try {
      const current = await CaisseController.current();
      if (!current || !current.id) {
        // Caisse was closed successfully
        setCaisse(null);
        clearCart();
        setCaisseCycle((n) => n + 1);
        setShowOpenModal(true);
      }
    } catch {
      // No open caisse means it was closed
      setCaisse(null);
      clearCart();
      setCaisseCycle((n) => n + 1);
      setShowOpenModal(true);
    }
  };

  /* ------------------------------- UI -------------------------------- */

  const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] flex flex-col bg-gray-50">
      {/* Top bar with caisse info */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-3 sm:px-4 py-3 bg-white border-b border-gray-200">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Caisse</h1>
          {caisse && (
            <div className="text-[11px] sm:text-xs text-gray-500 leading-snug break-words">
              Session #{caisse.id} • Dépôt: <span className="font-medium text-gray-700">{caisse.depot?.name || `#${caisse.depotId}`}</span> • Fond: <span className="font-medium">{formatTND(caisse.fondCaisse)}</span> • Total: <span className="font-medium text-emerald-700">{formatTND(caisse.montantTotal)}</span>
            </div>
          )}
        </div>
        {caisse && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={exchangeMode ? cancelExchange : startExchange}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold text-white ${exchangeMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
            >
              {exchangeMode ? 'Quitter échange' : 'Échange'}
            </button>
            <button
              onClick={loadPreorders}
              disabled={loadingPreorders}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
            >
              {loadingPreorders ? 'Chargement…' : 'Précommandes'}
            </button>
            <button
              onClick={closeCaisse}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700"
            >
              Fermer la caisse
            </button>
          </div>
        )}
      </div>

      {/* Main split: products | cart */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-3 sm:gap-4 p-3 sm:p-4 lg:overflow-hidden">
        {/* LEFT: search + product grid */}
        <div className="flex flex-col bg-white rounded-xl shadow-sm overflow-hidden min-h-[60vh] lg:min-h-0">
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Rechercher / scanner un code-barres (entrée pour ajouter)…"
                className="w-full px-4 py-4 pl-12 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#174540] focus:border-[#174540]"
                disabled={!caisse}
              />
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
              {searchDraft && (
                <button onClick={() => setSearchDraft('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {!caisse ? (
              <div className="h-full flex items-center justify-center text-gray-500">
                Ouvrez une caisse pour commencer.
              </div>
            ) : loadingProducts && products.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-500">Chargement…</div>
            ) : products.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-500">Aucun produit trouvé.</div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  const grouped = groupProductsByProductId(products);
                  return Object.values(grouped).map((group) => {
                    const parent = group.parent;
                    const variants = group.variants;
                    
                    // If there are variants (with or without parent)
                    if (variants.length > 0) {
                      // Extract product name from first variant if no parent
                      const productName = parent?.name || variants[0]?.name?.split(' — ')[0] || 'Product';
                      const productImage = parent?.imageUrl || variants[0]?.imageUrl;
                      return (
                        <div key={parent?.productId || variants[0]?.productId} className="space-y-2">
                          <div className="font-semibold text-sm text-gray-700 px-2 py-1">
                            📦 {productName}
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                            {variants.map((v) => (
                              <ProductCard
                                key={`${v.productId}:${v.productVariantId ?? 0}`}
                                product={{ ...v, imageUrl: productImage }}
                                canReturnOutOfStock={exchangeMode && addTarget === 'return'}
                                onAdd={addToCart}
                                isVariant={true}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    }
                    
                    // If there's only a parent (no variants), show it alone
                    if (parent && variants.length === 0) {
                      return (
                        <div key={parent.productId} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                          <ProductCard
                            key={`${parent.productId}:0`}
                            product={parent}
                            canReturnOutOfStock={exchangeMode && addTarget === 'return'}
                            onAdd={addToCart}
                            isVariant={false}
                          />
                        </div>
                      );
                    }
                    
                    return null;
                  });
                })()}
              </div>
            )}
          </div>

          {/* Pagination */}
          {caisse && total > PAGE_SIZE && (
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t border-gray-200 bg-gray-50">
              <button
                disabled={page <= 1 || loadingProducts}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 sm:px-4 py-2 text-sm rounded-md border border-gray-300 bg-white disabled:opacity-50"
              >
                ← Préc.
              </button>
              <span className="order-last sm:order-none w-full sm:w-auto text-center text-xs sm:text-sm text-gray-600">Page {page} / {maxPage} • {total} résultat(s)</span>
              <button
                disabled={page >= maxPage || loadingProducts}
                onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                className="px-3 sm:px-4 py-2 text-sm rounded-md border border-gray-300 bg-white disabled:opacity-50"
              >
                Suiv. →
              </button>
            </div>
          )}
        </div>

        {/* RIGHT: cart */}
        <div className="flex flex-col bg-white rounded-xl shadow-sm overflow-hidden min-h-[50vh] lg:min-h-0">
          {editingPreorder && (
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
              <div className="text-xs text-amber-800">
                <span className="font-semibold">Édition précommande</span> {editingPreorder.reference}
                {editingPreorder.previousMontantPaye > 0 && (
                  <span> • Déjà payé: <span className="font-semibold">{formatTND(editingPreorder.previousMontantPaye)}</span></span>
                )}
              </div>
              <button onClick={cancelEditingPreorder} className="text-xs font-medium text-amber-800 hover:underline">
                Annuler édition
              </button>
            </div>
          )}
          {exchangeMode && (
            <div className="bg-indigo-50 border-b border-indigo-200">
              <div className="px-4 py-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-indigo-800">Mode échange</span>
                <button onClick={cancelExchange} className="text-xs font-medium text-indigo-800 hover:underline">
                  Annuler
                </button>
              </div>
              <div className="px-4 pb-3">
                <div className="text-xs text-indigo-700 mb-1">Ajouter le produit cliqué à :</div>
                <div className="flex rounded-lg overflow-hidden border border-indigo-300">
                  <button
                    onClick={() => setAddTarget('return')}
                    className={`flex-1 py-2 text-sm font-semibold ${addTarget === 'return' ? 'bg-indigo-600 text-white' : 'bg-white text-indigo-700'}`}
                  >
                    Retours
                  </button>
                  <button
                    onClick={() => setAddTarget('new')}
                    className={`flex-1 py-2 text-sm font-semibold ${addTarget === 'new' ? 'bg-indigo-600 text-white' : 'bg-white text-indigo-700'}`}
                  >
                    Nouveaux
                  </button>
                </div>
              </div>
              <div className="px-4 pb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-indigo-800">
                    Produits retournés ({returnCart.reduce((s, it) => s + it.qty, 0)})
                  </span>
                  {returnCart.length > 0 && (
                    <button onClick={clearReturnCart} className="text-xs text-red-600 hover:underline">Vider</button>
                  )}
                </div>
                {returnCart.length === 0 ? (
                  <div className="text-xs text-indigo-500 py-2">Aucun produit retourné.</div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {returnCart.map((it) => (
                      <div key={it.key} className="flex items-center gap-2 bg-white rounded-lg border border-indigo-200 p-2">
                        <div className="h-10 w-10 rounded-md bg-gray-100 overflow-hidden shrink-0">
                          {it.imageUrl ? (
                            <img src={it.imageUrl} alt={it.name} className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#174540] to-[#0d2926] text-xs font-bold text-white">
                              {getProductInitials(it.name)}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{it.name}</div>
                          <div className="text-xs text-gray-500 truncate">{it.reference} • {formatTND(it.prix)}</div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => changeReturnQty(it.key, -1)} className="h-8 w-8 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">−</button>
                          <span className="w-6 text-center text-sm font-semibold">{it.qty}</span>
                          <button onClick={() => changeReturnQty(it.key, +1)} className="h-8 w-8 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">+</button>
                        </div>
                        <div className="w-16 text-right text-sm font-semibold text-gray-900 shrink-0">{formatTND(it.prix * it.qty)}</div>
                        <button onClick={() => removeFromReturn(it.key)} className="text-gray-400 hover:text-red-600 shrink-0" title="Supprimer">✕</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-baseline justify-between mt-2 pt-2 border-t border-indigo-200">
                  <span className="text-xs text-indigo-700">Total retours</span>
                  <span className="text-sm font-bold text-indigo-800">{formatTND(exchangeTotals.returnTotal)}</span>
                </div>
              </div>
            </div>
          )}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {exchangeMode ? `Nouveaux produits (${totals.totalItems})` : `Panier (${totals.totalItems})`}
            </h2>
            {cart.length > 0 && (
              <button onClick={clearCart} className="text-xs text-red-600 hover:underline">Vider</button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {cart.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 p-8 text-center">
                <svg className="h-12 w-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-1.5 6h13" />
                </svg>
                Aucun article. Touchez un produit pour l'ajouter.
              </div>
            ) : cart.map((it) => (
              <div key={it.key} className="flex flex-wrap items-center gap-2 sm:gap-3 p-3">
                <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                  {it.imageUrl ? (
                    <img src={it.imageUrl} alt={it.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#174540] to-[#0d2926] text-sm font-bold tracking-wide text-white">
                      {getProductInitials(it.name)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{it.name}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {it.reference} • {formatTND(getLineUnitPrice(it))}
                    {it.enPromo && Number(it.prixOriginal) > Number(it.prix) && (
                      <>
                        {' '}<span className="text-gray-400 line-through">{formatTND(it.prixOriginal)}</span>
                        {' '}<span className="text-rose-600 font-semibold">Promo</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0 min-w-[140px]">
                  {editingPriceKey === it.key ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.001"
                        min="0"
                        value={priceDrafts[it.key] ?? ''}
                        onChange={(e) => setPriceDrafts((prev) => ({ ...prev, [it.key]: e.target.value }))}
                        onBlur={() => saveLinePrice(it.key)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            saveLinePrice(it.key);
                          }
                          if (e.key === 'Escape') {
                            cancelPriceEditor(it.key);
                          }
                        }}
                        autoFocus
                        className="w-28 px-2 py-1 text-sm border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      />
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          saveLinePrice(it.key);
                        }}
                        className="px-2 py-1 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                      >
                        OK
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openPriceEditor(it)}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200"
                      >
                        Remise
                      </button>
                      {Number(it.prixVente ?? it.prix) !== Number(it.prix) && (
                        <button
                          type="button"
                          onClick={() => resetLinePrice(it.key)}
                          className="px-2 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                        >
                          Réinitialiser
                        </button>
                      )}
                    </div>
                  )}
                  <div className="text-right text-xs text-gray-500">
                    Prix vente: <span className="font-semibold text-gray-700">{formatTND(getLineUnitPrice(it))}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => changeQty(it.key, -1)} className="h-9 w-9 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 text-lg">−</button>
                  <span className="w-7 sm:w-8 text-center text-base font-semibold">{it.qty}</span>
                  <button onClick={() => changeQty(it.key, +1)} className="h-9 w-9 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 text-lg">+</button>
                </div>
                <div className="w-20 text-right text-sm font-semibold text-gray-900 shrink-0">{formatTND(getLineUnitPrice(it) * it.qty)}</div>
                <button onClick={() => removeFromCart(it.key)} className="text-gray-400 hover:text-red-600 ml-1 shrink-0" title="Supprimer">✕</button>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-200 p-4 bg-gray-50">
            {exchangeMode ? (
              <>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-500">Total nouveaux</span>
                  <span className="text-lg font-semibold text-[#174540]">{formatTND(exchangeTotals.newTotal)}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-500">Total retours</span>
                  <span className="text-lg font-semibold text-indigo-700">− {formatTND(exchangeTotals.returnTotal)}</span>
                </div>
                <div className="flex items-baseline justify-between mt-2 pt-2 border-t border-gray-300">
                  <span className="text-sm font-semibold text-gray-700">
                    {exchangeTotals.difference > 0
                      ? 'Le client paie'
                      : exchangeTotals.difference < 0
                        ? 'À rendre au client'
                        : 'Aucune différence'}
                  </span>
                  <span className={`text-2xl font-bold ${exchangeTotals.difference < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                    {formatTND(Math.abs(exchangeTotals.difference))}
                  </span>
                </div>
                <button
                  type="button"
                  disabled={savingExchange || !caisse || (returnCart.length === 0 && cart.length === 0)}
                  onClick={confirmExchange}
                  className="mt-3 w-full py-4 rounded-xl text-lg font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingExchange ? 'Enregistrement…' : "Valider l'échange"}
                </button>
              </>
            ) : (
              <>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-500">Total TTC</span>
                  <span className="text-2xl font-bold text-[#174540]">{formatTND(totals.totalTTC)}</span>
                </div>
                <button
                  type="button"
                  disabled={cart.length === 0 || !caisse}
                  onClick={() => setCheckoutOpen(true)}
                  className="mt-3 w-full py-4 rounded-xl text-lg font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Encaisser
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <OpenCaisseModal
        key={`open-caisse-${caisseCycle}`}
        open={showOpenModal && !loadingCaisse}
        onCancel={() => {
          setShowOpenModal(false);
          navigate('/admin');
        }}
        onConfirm={async (vals) => {
          try {
            const c = await CaisseController.open(vals);
            setCaisse(c);
            setShowOpenModal(false);
            toast.success('Caisse ouverte');
          } catch (e) {
            console.error(e);
            toast.error('Impossible d\'ouvrir la caisse');
          }
        }}
      />

      <CheckoutModal
        open={checkoutOpen}
        total={totals.totalTTC}
        saving={savingSale}
        alreadyPaid={editingPreorder?.previousMontantPaye || 0}
        editingRef={editingPreorder?.reference || null}
        onCancel={() => setCheckoutOpen(false)}
        onConfirm={confirmSale}
      />

      <CaisseClosureModal
        key={`closure-${caisseCycle}`}
        isOpen={showClosureModal}
        onClose={handleClosureModalClose}
      />

      {/* Preorders Modal */}
      {showPreorders && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 bg-gradient-to-br from-amber-600 to-amber-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Précommandes à encaisser</h2>
              <button onClick={() => setShowPreorders(false)} className="text-white/80 hover:text-white text-xl">✕</button>
            </div>
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <input
                type="text"
                value={preorderSearch}
                onChange={(e) => setPreorderSearch(e.target.value)}
                placeholder="Rechercher par téléphone, nom ou référence…"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-amber-500"
                autoFocus
              />
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {(() => {
                const q = preorderSearch.trim().toLowerCase();
                const filtered = q
                  ? preorders.filter((o) => {
                      const phone = (o.clientNumber || '').toLowerCase();
                      const name = `${o.clientFirstName || ''} ${o.clientLastName || ''}`.trim().toLowerCase();
                      const ref = (o.reference || '').toLowerCase();
                      return phone.includes(q) || name.includes(q) || ref.includes(q);
                    })
                  : preorders;
                if (filtered.length === 0) {
                  return <div className="p-8 text-center text-gray-500">{q ? 'Aucun résultat.' : 'Aucune précommande en attente.'}</div>;
                }
                return filtered.map((o) => (
                <div key={o.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{o.reference || `#${o.id}`}</div>
                    <div className="text-xs text-gray-500">
                      {`${o.clientFirstName || ''} ${o.clientLastName || ''}`.trim() || 'Client anonyme'}
                      {o.clientNumber ? ` • ${o.clientNumber}` : ''}
                    </div>
                    <div className="text-xs text-gray-400">
                      {o.dateCommande ? new Date(o.dateCommande).toLocaleDateString('fr-FR') : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-900">{formatTND(o.totalTTC)}</div>
                      <div className="text-xs text-amber-700">Reste: {formatTND(o.totalTTC - (o.montantPaye || 0))}</div>
                    </div>
                    <button
                      onClick={() => payPreorder(o)}
                      className="px-3 py-1.5 text-xs font-semibold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700"
                    >
                      Charger
                    </button>
                  </div>
                </div>
              ));
              })()}
            </div>
            <div className="px-6 py-3 bg-gray-50 border-t">
              <button onClick={() => setShowPreorders(false)} className="w-full py-2 rounded-lg text-sm font-medium border border-gray-300 hover:bg-gray-100">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaissePage;
